import React from 'react';

import {
  AnalyticsError,
  analyticsConfigured,
  fetchAnalyticsHosts,
  runAggregate,
  type AggregateQuery,
  type AnalyticsHost,
  type AnalyticsRow,
} from './api';
import {
  attributedParts,
  attributionActive,
  avgSecondsPerVisit,
  sourcesQuery,
  devicesQuery,
  fillTrend,
  inCountries,
  NO_ATTRIBUTION,
  creditedOnly,
  selectionIssue,
  fitsFilters,
  type Match,
  linkReachQuery,
  linksQuery,
  mergeAttributed,
  narrowToRows,
  pagePaths,
  pageTimeQuery,
  pageViewsQuery,
  scoped,
  shapeSources,
  shapeSourceReach,
  sourceReachQuery,
  channelReachQuery,
  shapeDevices,
  shapeHosts,
  shapeLinkReach,
  shapeLinks,
  shapePages,
  shapeSystems,
  sumTotals,
  systemsQuery,
  timeQuery,
  topPagesQuery,
  totalsByHostQuery,
  trendQuery,
  viewsByPath,
  websiteWindow,
  type Attribution,
  type SourceRow,
  type SourceReach,
  type DeviceRow,
  type PageScope,
  type HostRow,
  type Span,
  type LinkRow,
  type PageRow,
  type SystemRow,
  type Totals,
  type TrendPoint,
  type WebsitePeriod,
  type WebsiteScope,
  type WebsiteWindow,
} from './queries';
import type { TrafficQualityMode } from './quality/gate';
import { canonicalSelection, emptyLinks, selectionEmpty } from './selection';

/** One card's data: still loading, failed, or here. */
export type Section<T> = { status: 'loading' | 'error' | 'ready'; data: T | null };

/**
 * Where the tab is as a whole:
 *  - `loading`     the brand's addresses are being read;
 *  - `unavailable` website stats are not set up in this environment;
 *  - `no-site`     the brand has no live address yet;
 *  - `error`       the addresses could not be read;
 *  - `ready`       the cards load on their own from here.
 */
export type WebsitePhase = 'loading' | 'unavailable' | 'no-site' | 'error' | 'ready';

export type WebsiteAnalytics = {
  phase: WebsitePhase;
  hosts: AnalyticsHost[];
  /** This environment can credit visits to a source or a link (the source / link filters and their breakdowns). */
  attributionAvailable: boolean;
  conditionsAvailable: boolean;
  error: string | null;
  window: WebsiteWindow;
  totals: Section<Totals>;
  previousTotals: Section<Totals>;
  /** Average seconds per visit (null: nothing measured). */
  time: Section<number | null>;
  previousTime: Section<number | null>;
  trend: Section<TrendPoint[]>;
  pages: Section<PageRow[]>;
  devices: Section<DeviceRow[]>;
  systems: Section<SystemRow[]>;
  sites: Section<HostRow[]>;
  /** The same splits in the period before, for each item's change. */
  previousDevices: Section<DeviceRow[]>;
  previousSystems: Section<SystemRow[]>;
  previousSites: Section<HostRow[]>;
  /** Visitors per source and platform (within the picked link), and in the period before; per link (within the picked
   *  source). Only read where {@link WebsiteAnalytics.attributionAvailable}. */
  sources: Section<SourceRow[]>;
  previousSources: Section<SourceRow[]>;
  links: Section<LinkRow[]>;
  /** Each source's and platform's arrivals within the picked link (the source picker's counts, what picking one
   *  shows); empty with "no link" picked, where a picked source shows its credited visitors. */
  sourceReach: Section<SourceReach>;
  /** The figures answer the brand, period and filters asked now (false for the render between a change and the read it
   *  starts, which still holds the last read's figures). */
  current: boolean;
  reload: () => void;
};

// The analytics service answers a handful of queries at once for everyone and refuses (rather than
// queues) the rest, so the tab asks two at a time.
const CONCURRENCY = 2;

const loading = <T>(): Section<T> => ({ status: 'loading', data: null });

type SectionKey =
  | 'totals'
  | 'previousTotals'
  | 'time'
  | 'previousTime'
  | 'trend'
  | 'pages'
  | 'devices'
  | 'systems'
  | 'sites'
  | 'previousDevices'
  | 'previousSystems'
  | 'previousSites'
  | 'sources'
  | 'previousSources'
  | 'links'
  | 'sourceReach';

/** `key`: the brand, period and filters the figures answer; `scope`: the brand and mode the addresses belong to. */
type State = Omit<WebsiteAnalytics, 'reload' | 'window' | 'current'> & { key: string; scope: string };

const initialState = (): State => ({
  key: '',
  scope: '',
  phase: 'loading',
  hosts: [],
  attributionAvailable: false,
  conditionsAvailable: false,
  error: null,
  totals: loading(),
  previousTotals: loading(),
  time: loading(),
  previousTime: loading(),
  trend: loading(),
  pages: loading(),
  devices: loading(),
  systems: loading(),
  sites: loading(),
  previousDevices: loading(),
  previousSystems: loading(),
  previousSites: loading(),
  sources: loading(),
  previousSources: loading(),
  links: loading(),
  sourceReach: loading(),
});

/** The Website tab's filters, as its queries take them: some pages (or every page but some), the sources and links
 *  (an attribution), and some countries (or every country but some). */
export type WebsiteQuery = {
  pages: readonly PageScope[];
  /** Every page but `pages`. */
  pagesOut?: boolean;
  attribution: Attribution;
  countries: Match | null;
};

export const NO_QUERY: WebsiteQuery = { pages: [], attribution: NO_ATTRIBUTION, countries: null };

/** The pages picked, as the queries narrow to them: their paths on the brand's addresses of their kinds (landing,
 *  website, or both when both kinds are picked); `out`: every page but those, on every address. Pure. */
export const scopeOf = (pages: readonly PageScope[], hosts: AnalyticsHost[], out = false): WebsiteScope | null => {
  if (pages.length === 0) return null;
  if (out) return { paths: pages.map((p) => p.path), hosts: [], exclude: true };
  const kinds = new Set(pages.map((p) => (p.kind === 'landing' ? 'landing' : 'site')));
  return { paths: pages.map((p) => p.path), hosts: hosts.filter((h) => kinds.has(h.kind)).map((h) => h.host) };
};

export type Asker = {
  /** One query as it is, narrowed to the page and the country. */
  askOne: (query: AggregateQuery) => Promise<AnalyticsRow[]>;
  /** One card's question: as it is, or (a source or link filter on, where the environment attributes) as its attributed
   *  parts, asked one after the other (the service refuses rather than queues a burst) and joined. `ranked`: a top list,
   *  whose first part's rows are the answer (see mergeAttributed). */
  ask: (query: AggregateQuery, ranked?: boolean) => Promise<AnalyticsRow[]>;
};

/**
 * How the tab asks the analytics service, shared by the cards and the filters' counts so both read alike. `scope` and
 * `attributing` are read at each ask (the cards learn them only once the brand's addresses are in).
 */
export const makeAsker = (opts: {
  tenantId: number;
  signal: AbortSignal;
  attr: () => Attribution;
  countries: Match | null;
  scope: () => WebsiteScope | null;
  attributing: () => boolean;
  conditionsAvailable?: () => boolean;
}): Asker => {
  const prepare = (q: AggregateQuery): AggregateQuery => inCountries(scoped(q, opts.scope()), opts.countries);
  const send = async (q: AggregateQuery): Promise<AnalyticsRow[]> => {
    if (selectionEmpty(opts.attr()) || !q.metrics.length) return [];
    const issue = selectionIssue(opts.attr(), opts.conditionsAvailable?.() ?? false);
    if (issue)
      throw new AnalyticsError(
        issue === 'limit'
          ? 'Too many filter conditions. Remove a choice to continue.'
          : 'This environment supports one choice per filter.',
        400,
      );
    if (q.conditions && !opts.conditionsAvailable?.())
      throw new AnalyticsError('This environment supports one choice per filter.', 400);
    if (
      (q.attribution?.unit === 'visitor' || q.attribution?.unit === 'session') &&
      (q.timeBucket ||
        q.filters?.some((f) => f.field === 'properties.page.path') ||
        q.groupBy?.includes('properties.page.path'))
    )
      return [];
    return (await runAggregate(opts.tenantId, q, opts.signal)).rows;
  };
  const askOne = (q: AggregateQuery): Promise<AnalyticsRow[]> => send(prepare(q));
  const ask = async (query: AggregateQuery, ranked = false): Promise<AnalyticsRow[]> => {
    if (!opts.attributing()) return askOne(query);
    const [first, ...rest] = attributedParts(prepare(query), opts.attr());
    if (!first) return [];
    const answers = [await send(first)];
    if (ranked && answers[0].length === 0) return [];
    const narrow = narrowToRows(query, answers[0], ranked);
    for (const part of rest)
      answers.push(await send(narrow ? { ...part, filters: [...(part.filters ?? []), narrow] } : part));
    return mergeAttributed(query, answers, ranked);
  };
  return { askOne, ask };
};
type AskOne = Asker['askOne'];

export const expandLinkExclusions = async (
  askOne: AskOne,
  spans: readonly Span[],
  attr: Attribution,
): Promise<Attribution> => {
  const affiliates = attr.links?.exceptAffiliates ?? [];
  if (attr.campaigns?.op !== 'out' && !affiliates.length) return attr;
  const ids = new Set<string>(attr.links?.allExcept ?? []);
  for (const span of spans) {
    const credited = linksQuery(span, attr);
    const events: AggregateQuery = {
      ...credited,
      attribution: { unit: 'event' },
      metrics: [{ name: 'visitors', op: 'count' }],
      filters: credited.filters?.filter((f) => f.field !== 'event_type'),
    };
    for (const query of [credited, events, linkReachQuery(span, attr)]) {
      const rows = await askOne(query);
      if (rows.length >= (query.limit ?? 1000))
        throw new AnalyticsError('Too many filter conditions. Remove a choice to continue.', 400);
      const links = query.attribution ? shapeLinks(rows) : shapeLinkReach(rows);
      links
        .filter(
          (r) =>
            (affiliates.some((id) => r.affId.startsWith(`${id}_`)) ||
              attr.campaigns?.values.some((id) => r.affId.includes(`_${id}_`))) &&
            !attr.links?.exact.includes(r.affId),
        )
        .forEach((r) => ids.add(r.affId));
    }
  }
  if (ids.size > 50) throw new AnalyticsError('Too many filter conditions. Remove a choice to continue.', 400);
  return {
    ...attr,
    campaigns: null,
    forceCredited: true,
    links: { ...emptyLinks(), noLink: attr.links?.noLink ?? true, allExcept: [...ids] },
  };
};

/** Each link with its reach (what picking it shows), and "no link" as credited (no reach to speak of). Within the
 *  Unknown source picking a link shows it credited, so every link is counted that way. */
export const readLinks = async (askOne: AskOne, span: Span, attr: Attribution): Promise<LinkRow[]> => {
  const credited = shapeLinks(await askOne(linksQuery(span, attr)));
  if (creditedOnly(attr)) return credited;
  const reach = shapeLinkReach(await askOne(linkReachQuery(span, attr)));
  return [...credited.filter((l) => l.affId === ''), ...reach];
};

/** The arrivals each source and platform brought (none with "no link" picked, which no arrival names). */
export const readSourceReach = async (askOne: AskOne, span: Span, attr: Attribution): Promise<SourceReach> =>
  creditedOnly(attr)
    ? { channels: [], platforms: [] }
    : shapeSourceReach(await askOne(sourceReachQuery(span, attr)), await askOne(channelReachQuery(span, attr)));

export const runPool = async (tasks: Array<() => Promise<void>>, size: number): Promise<void> => {
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < tasks.length) {
      const task = tasks[next];
      next += 1;
      await task();
    }
  };
  await Promise.all(Array.from({ length: Math.min(size, tasks.length) }, worker));
};

/**
 * The Website tab's data for one brand and period. The brand's addresses are read first, then each card's
 * query runs (two at a time) and lands on its own, so the page fills in progressively. Every load gets an
 * id from a growing counter and only the newest load's answers land (a brand switch or a new period
 * mid-load never shows the older answer). The address list is kept per brand and a failed re-read keeps
 * the last good one.
 *
 * `query.pages` narrows every card to some pages: landing pages (their paths on the brand's landing addresses) and / or
 * pages of the website (their paths on the website's addresses), or every page but those. The most visited pages are
 * then not asked for.
 *
 * `query.attribution` narrows every card to some sources and/or links, affiliates or campaigns: each query is then
 * asked as attributed parts and their answers joined (see {@link attributedParts}). Where the environment cannot
 * attribute, it is ignored. `query.countries` narrows every card to some countries (no attribution needed).
 *
 * In `quality` mode the figures come from the quality report instead: only the brand's addresses are read (the
 * address list and what this environment can filter by), no aggregate request is made, and the cards stay unread.
 * Every cache and the figures kept across a brand's re-reads belong to one brand in one mode.
 */
export const useWebsiteAnalytics = (
  tenantId: number | undefined,
  interval: WebsitePeriod,
  query: WebsiteQuery = NO_QUERY,
  mode: TrafficQualityMode = 'legacy',
): WebsiteAnalytics => {
  // The filters by value: a new object with the same choices is the same load.
  const queryKey = JSON.stringify({
    pages: [...query.pages].sort((a, b) => `${a.kind}:${a.path}`.localeCompare(`${b.kind}:${b.path}`)),
    pagesOut: !!query.pagesOut,
    countries: query.countries ? { op: query.countries.op, values: [...query.countries.values].sort() } : null,
    attribution: {
      ...canonicalSelection(query.attribution),
      campaigns: query.attribution.campaigns
        ? { op: query.attribution.campaigns.op, values: [...query.attribution.campaigns.values].sort() }
        : null,
      forceCredited: !!query.attribution.forceCredited,
    },
  });
  // What a read answers: the brand, the mode, the period and the filters.
  const loadKey = JSON.stringify([tenantId, mode, interval, queryKey]);
  const scopeKey = `${tenantId}:${mode}`;
  // Per brand and mode: whether its environment attributes (from the address read), kept across re-reads.
  const attributionCache = React.useRef(new Map<string, boolean>());
  const conditionsCache = React.useRef(new Map<string, boolean>());
  const lastScope = React.useRef<string>();
  const [state, setState] = React.useState<State>(initialState);
  const [nonce, setNonce] = React.useState(0);
  const readId = React.useRef(0);
  const hostsCache = React.useRef(new Map<string, AnalyticsHost[]>());
  // The window is fixed per load (so "now" does not drift between the queries of one load).
  const [period, setPeriod] = React.useState<WebsiteWindow>(() => websiteWindow(interval, Date.now()));

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  React.useEffect(() => {
    if (tenantId === undefined) return undefined;
    readId.current += 1;
    const id = readId.current;
    const controller = new AbortController();
    const { signal } = controller;
    const live = (): boolean => id === readId.current && !signal.aborted;
    const win = websiteWindow(interval, Date.now());
    setPeriod(win);
    // The brand's addresses stay known across its re-reads in one mode (the page picker lists from them).
    const sameBrand = lastScope.current === scopeKey;
    lastScope.current = scopeKey;
    setState((prev) => ({
      ...(sameBrand ? prev : initialState()),
      ...Object.fromEntries(
        Object.entries(initialState())
          .filter(([, value]) => value && typeof value === 'object' && 'status' in value)
          .map(([key]) => [key, { status: 'loading', data: sameBrand ? prev[key as SectionKey].data : null }]),
      ),
      key: loadKey,
      scope: scopeKey,
      phase: 'loading',
      error: null,
      hosts: hostsCache.current.get(scopeKey) ?? [],
      attributionAvailable: attributionCache.current.get(scopeKey) ?? false,
      conditionsAvailable: conditionsCache.current.get(scopeKey) ?? false,
    }));
    const fail = (key: SectionKey, error: unknown): void => {
      if (live())
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : 'We could not load this. Try again in a moment.',
          [key]: { status: prev[key].data !== null ? 'ready' : 'error', data: prev[key].data },
        }));
    };

    const land = <K extends SectionKey>(key: K, section: State[K]): void => {
      if (live()) setState((s) => ({ ...s, [key]: section }));
    };
    let scope: WebsiteScope | null = null;
    const filtersNow = JSON.parse(queryKey) as WebsiteQuery;
    let attr = filtersNow.attribution;
    let attributing = false;
    const { askOne, ask } = makeAsker({
      tenantId,
      signal,
      attr: () => attr,
      countries: filtersNow.countries,
      scope: () => scope,
      attributing: () => attributing,
      conditionsAvailable: () => conditionsCache.current.get(scopeKey) ?? false,
    });
    const card =
      <K extends SectionKey>(key: K, load: () => Promise<State[K]['data']>) =>
      async (): Promise<void> => {
        try {
          const data = await load();
          land(key, { status: 'ready', data } as State[K]);
        } catch (error) {
          fail(key, error);
        }
      };

    const run = async (): Promise<void> => {
      if (!analyticsConfigured()) {
        if (live()) setState((s) => ({ ...s, phase: 'unavailable' }));
        return;
      }
      let hosts = hostsCache.current.get(scopeKey);
      try {
        const answer = await fetchAnalyticsHosts(tenantId, signal);
        if (!answer.configured) {
          if (live()) setState((s) => ({ ...s, phase: 'unavailable' }));
          return;
        }
        hosts = answer.hosts;
        hostsCache.current.set(scopeKey, hosts);
        attributionCache.current.set(scopeKey, answer.attribution);
        conditionsCache.current.set(scopeKey, answer.conditions);
      } catch {
        if (!hosts) {
          if (live()) setState((s) => ({ ...s, phase: 'error' }));
          return;
        }
      }
      if (!live()) return;
      if (hosts.length === 0) {
        setState((s) => ({ ...s, phase: 'no-site', hosts: [] }));
        return;
      }
      const canAttribute = attributionCache.current.get(scopeKey) ?? false;
      attributing = canAttribute && attributionActive(attr);
      setState((s) => ({
        ...s,
        phase: 'ready',
        hosts: hosts ?? [],
        attributionAvailable: canAttribute,
        conditionsAvailable: conditionsCache.current.get(scopeKey) ?? false,
      }));
      if (mode === 'quality') return;
      scope = scopeOf(filtersNow.pages, hosts, !!filtersNow.pagesOut);

      if (attributing) {
        try {
          attr = await expandLinkExclusions(askOne, [win.current, win.previous], attr);
        } catch (error) {
          if (live()) setState((s) => ({ ...s, phase: 'error', error: error instanceof Error ? error.message : null }));
          return;
        }
        if (!live()) return;
      }

      await runPool(
        [
          async () => {
            try {
              const rows = await ask(totalsByHostQuery(win.current));
              land('totals', { status: 'ready', data: sumTotals(rows) });
              land('sites', { status: 'ready', data: shapeHosts(rows) });
            } catch (error) {
              fail('totals', error);
              fail('sites', error);
            }
          },
          card('time', async () => avgSecondsPerVisit(await ask(timeQuery(win.current)))),
          card('trend', async () => fillTrend(await ask(trendQuery(win.current, win.bucket)), win.buckets)),
          async () => {
            try {
              const rows = await ask(totalsByHostQuery(win.previous));
              land('previousTotals', { status: 'ready', data: sumTotals(rows) });
              land('previousSites', { status: 'ready', data: shapeHosts(rows) });
            } catch (error) {
              fail('previousTotals', error);
              fail('previousSites', error);
            }
          },
          card('previousTime', async () => avgSecondsPerVisit(await ask(timeQuery(win.previous)))),
          card('pages', async () => {
            // Some pages picked: their own figures stand in for the list. Every page but some still lists the rest,
            // unless the filters on would ask more than the service takes (the page leaves the list out then too).
            const fits = fitsFilters(topPagesQuery(win.current), attr, attributing, scope, filtersNow.countries, true);
            if (scope && (!scope.exclude || !fits)) return [];
            const rows = await ask(topPagesQuery(win.current), true);
            const paths = pagePaths(rows);
            // The time per page is a second question about the same pages; a failure there still shows the list.
            const timeRows = paths.length > 0 ? await ask(pageTimeQuery(win.current, paths)).catch(() => []) : [];
            // The same pages' views in the period before; a failure only drops their change.
            const before = paths.length > 0 ? await ask(pageViewsQuery(win.previous, paths)).catch(() => null) : [];
            const previous = new Map(viewsByPath(before ?? []).map((p) => [p.path, p.views]));
            return shapePages(rows, timeRows).map((p) => ({
              ...p,
              previousViews: before === null ? null : previous.get(p.path) ?? 0,
            }));
          }),
          card('devices', async () => shapeDevices(await ask(devicesQuery(win.current)))),
          card('systems', async () => shapeSystems(await ask(systemsQuery(win.current)))),
          card('previousDevices', async () => shapeDevices(await ask(devicesQuery(win.previous)))),
          card('previousSystems', async () => shapeSystems(await ask(systemsQuery(win.previous)))),
          // The source and link breakdowns are attributed questions of their own (asked as they are).
          ...(canAttribute
            ? [
                card('sources', async () => shapeSources(await askOne(sourcesQuery(win.current, attr)))),
                card('links', () => readLinks(askOne, win.current, attr)),
                card('previousSources', async () => shapeSources(await askOne(sourcesQuery(win.previous, attr)))),
                card('sourceReach', () => readSourceReach(askOne, win.current, attr)),
              ]
            : []),
        ],
        CONCURRENCY,
      );
    };

    void run();
    return () => controller.abort();
  }, [tenantId, mode, interval, nonce, queryKey, loadKey, scopeKey]);

  // Another brand's or mode's addresses and figures are never shown, not even for the render before its read starts.
  const { key, scope, ...figures } = state.scope === scopeKey ? state : initialState();
  return { ...figures, current: key === loadKey && !state.error, window: period, reload };
};
